import type { InferOutput } from "valibot";
import { array, literal, object, optional, union, variant } from "valibot";
import type { IncompleteIntegrationDefinition } from "../types";
import { initIntegrationDefinition } from "../Integration";
import type { TranslationKey } from "../../state/i18n";
import { useTranslation } from "../../state/i18n";
import ServiceAccountSelectInput from "../../components/inputs/ServiceAccountSelectInput";
import { TextInput } from "../../components/inputs/TextInput";
import { MAX_CHARACTERS_SM } from "../../constants";
import { requireText, serviceAccount } from "../../utils/validation/base";
import ServiceAccountService from "../../services/ServiceAccountService";
import { isEmpty, isEqual, isNil } from "lodash-es";
import ServiceAccountId from "../../state/ServiceAccountId";
import { integrationsIcons } from "../IntegrationIcons";
import type { VoidComponent } from "solid-js";
import { createMemo, createSignal, createUniqueId, For, Show } from "solid-js";
import { useFormServices } from "../../services/formServices";
import { toField } from "../../fields/fields";
import { TextAndIconButton, TextButton } from "../../components/button/Button";
import { DynamicValueIconTile } from "../../components/icon/IconTile";
import { fieldValueReferenceOfTypes, metaVariableValueReferenceOfTypes } from "../../utils/validation/references";
import FieldValueReference from "../../fields/FieldValueReference";
import Text from "../../components/text/Text";
import { useCurrentModal, useModalManager } from "../../components/modal/ModalManager";
import DetailModal from "../../components/modal/DetailModal";
import MetaVariableReference from "../../metaVariables/MetaVariableReference";
import FieldList from "../../fields/list/FieldList";
import SearchBar from "../../components/searchBar/SearchBar";
import type { MetaVariableId } from "../../metaVariables/metaVariable";
import { isMetaVariable, META_VARIABLES } from "../../metaVariables/metaVariable";
import { ErrorMessage, Header } from "../../components/inputs/Input";
import type { FieldType } from "../../types";
import InlineMessage from "../../components/inlineMessage/InlineMessage";
import { letterForNumber } from "../../utils/stringUtil";
import type { IconName } from "../../components/icon/Icon";
import { getIconCategoryColor } from "../../utils/iconColorUtil";
import { createWritableMemo } from "@solid-primitives/memo";
import { keyFromSelector } from "i18next";

type ConfigurationDto = {
  serviceAccount?: string;
  spreadsheetId?: string;
  sheetName: string;
  metadata?: ("location" | "serialNumber")[];
  columnOrder?: { type: "field" | "metaVariable"; value: string }[];
};

const ALLOWED_COLUMN_ORDER_FIELD_TYPES: FieldType[] = [
  "address",
  "barcode",
  "calculation",
  "checkbox",
  "currentLocation",
  "date",
  "dateTime",
  "email",
  "iban",
  "licensePlate",
  "location",
  "lookup",
  "number",
  "phone",
  "price",
  "radio",
  "rating",
  "readOnlyText",
  "search",
  "slider",
  "smiley",
  "stopwatch",
  "subform",
  "text",
  "textArea",
  "time",
  "timeDifference",
];
const ALLOWED_COLUMN_ORDER_META_VARIABLES: MetaVariableId[] = [
  "meta.registrationDate",
  "info.userId",
  "meta.serialNumber",
  "meta.location.latitude",
  "meta.location.longitude",
];

const Schema = object({
  serviceAccount: serviceAccount(),
  spreadsheetId: requireText(MAX_CHARACTERS_SM),
  sheetName: requireText(MAX_CHARACTERS_SM),
  metadata: optional(array(union([literal("location"), literal("serialNumber")]))),
  columnOrder: optional(
    array(
      variant("type", [
        object({
          type: literal("field"),
          value: fieldValueReferenceOfTypes(ALLOWED_COLUMN_ORDER_FIELD_TYPES),
        }),
        object({
          type: literal("metaVariable"),
          value: metaVariableValueReferenceOfTypes(ALLOWED_COLUMN_ORDER_META_VARIABLES),
        }),
      ]),
    ),
  ),
});

type ColumnOrderOption = {
  id: FieldValueReference | MetaVariableReference;
  label: string;
  icon: IconName;
  iconClasses: string;
};

export type GoogleSheetsIntegrationConfiguration = InferOutput<typeof Schema>;
const GoogleSheetsIntegration: IncompleteIntegrationDefinition<GoogleSheetsIntegrationConfiguration, ConfigurationDto> =
  {
    type: "googlesheets",
    legacy: {
      namespace: "com.moreapps.plugins",
      key: "googlesheets",
      version: 8,
    },
    schema: Schema,
    icon: integrationsIcons["googlesheets"],
    titleKey: keyFromSelector(($) => $.GOOGLE_SHEETS_INTEGRATION_TITLE),
    descriptionKey: keyFromSelector(($) => $.GOOGLE_SHEETS_INTEGRATION_DESCRIPTION),
    verifiable: true,
    defaultProperties: () => {
      const { t } = useTranslation();

      return {
        serviceAccount: undefined,
        spreadsheetId: "",
        sheetName: t(($) => $.GOOGLE_SHEETS_INTEGRATION_CONFIGURATION_SHEET_NAME_DEFAULT_VALUE),
        columnOrder: [
          { type: "metaVariable", value: MetaVariableReference.from("meta.registrationDate") },
          { type: "metaVariable", value: MetaVariableReference.from("info.userId") },
          { type: "metaVariable", value: MetaVariableReference.from("meta.serialNumber") },
        ],
      };
    },
    propertyKeys: {
      serviceAccount: keyFromSelector(($) => $.GOOGLE_SHEETS_INTEGRATION_CONFIGURATION_SERVICE_ACCOUNT_LABEL),
      spreadsheetId: keyFromSelector(($) => $.GOOGLE_SHEETS_INTEGRATION_CONFIGURATION_SPREADSHEET_ID_LABEL),
      sheetName: keyFromSelector(($) => $.GOOGLE_SHEETS_INTEGRATION_CONFIGURATION_SHEET_NAME_LABEL),
      metadata: "" as TranslationKey,
      columnOrder: keyFromSelector(($) => $.GOOGLE_SHEETS_INTEGRATION_CONFIGURATION_COLUMN_ORDER_LABEL),
    },
    fromDto: (configuration) => ({
      serviceAccount: isNil(configuration.serviceAccount)
        ? undefined
        : ServiceAccountId.from(configuration.serviceAccount),
      spreadsheetId: configuration.spreadsheetId,
      sheetName: configuration.sheetName,
      metadata: configuration.metadata,
      columnOrder: configuration.columnOrder?.map(({ type, value }) => {
        if (type === "field") {
          return { type: "field", value: FieldValueReference.from(value) };
        } else if (type === "metaVariable" && isMetaVariable(value)) {
          return { type: "metaVariable", value: MetaVariableReference.from(value) };
        } else {
          throw new Error(`Invalid 'columnOrder' type/value (${type} - ${value})`);
        }
      }),
    }),
    toDto: (configuration) => ({
      serviceAccount: configuration.serviceAccount?.toString(),
      spreadsheetId: configuration.spreadsheetId,
      sheetName: configuration.sheetName,
      metadata: configuration.metadata,
      columnOrder: configuration.columnOrder?.map(({ type, value }) => ({ type, value: value.toString() })),
    }),
    EditItem: (props) => {
      const { t } = useTranslation();
      const { open } = useModalManager();
      const { fieldService } = useFormServices();

      const fieldOptions = fieldService.getFieldsByTypes(ALLOWED_COLUMN_ORDER_FIELD_TYPES);

      const allColumnOrderOptions: {
        id: FieldValueReference | MetaVariableReference;
        label: string;
        icon: IconName;
        iconClasses: string;
      }[] = [
        ...ALLOWED_COLUMN_ORDER_META_VARIABLES.map((metaVariableId) => ({
          id: new MetaVariableReference(metaVariableId),
          label: t(META_VARIABLES[metaVariableId].label) as string,
          icon: META_VARIABLES[metaVariableId].icon,
          iconClasses: "icon-primary bg-surface-subtle",
        })),
        ...fieldOptions.flatMap((fieldConfig) => {
          const fieldType = toField(fieldConfig.type);
          return fieldType
            .valueReferenceOptions(fieldConfig.uid, fieldConfig.properties)
            .filter(({ isSelectable }) => isSelectable !== false)
            .map(({ value, label }) => ({
              id: value,
              label,
              icon: fieldType.icon,
              iconClasses: getIconCategoryColor(fieldType.category),
            }));
        }),
      ];

      const columnOrder = createMemo(() =>
        props.integration.configuration.columnOrder
          ?.map(({ value }) => allColumnOrderOptions.find((option) => isEqual(option.id, value)))
          .filter((option) => !isNil(option)),
      );

      return (
        <>
          <ServiceAccountSelectInput
            type="GOOGLE_SHEETS"
            label={t(props.propertyKeys.serviceAccount)}
            value={props.integration.configuration.serviceAccount}
            fetchServiceAccounts={() => ServiceAccountService.getAll()}
            onChange={(value) => props.updateConfiguration("serviceAccount", value)}
            errorMessage={props.getError("serviceAccount")}
            required
            info={{
              title: t(props.propertyKeys.serviceAccount),
              alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, {
                property: props.propertyKeys.serviceAccount,
              }),
              description: t(($) => $.GOOGLE_SHEETS_INTEGRATION_CONFIGURATION_SERVICE_ACCOUNT_DESCRIPTION),
            }}
          />
          <TextInput
            label={t(props.propertyKeys.spreadsheetId)}
            value={props.integration.configuration.spreadsheetId}
            onBlur={(e) => props.updateConfiguration("spreadsheetId", e.target.value)}
            errorMessage={props.getError("spreadsheetId")}
            required
            info={{
              title: t(props.propertyKeys.spreadsheetId),
              alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, {
                property: props.propertyKeys.spreadsheetId,
              }),
              description: t(($) => $.GOOGLE_SHEETS_INTEGRATION_CONFIGURATION_SPREADSHEET_ID_DESCRIPTION),
            }}
          />
          <TextInput
            label={t(props.propertyKeys.sheetName)}
            value={props.integration.configuration.sheetName}
            onBlur={(e) => props.updateConfiguration("sheetName", e.target.value)}
            errorMessage={props.getError("sheetName")}
            required
            info={{
              title: t(props.propertyKeys.sheetName),
              alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.sheetName }),
              description: t(($) => $.GOOGLE_SHEETS_INTEGRATION_CONFIGURATION_SHEET_NAME_DESCRIPTION),
            }}
          />
          <div class="flex flex-col gap-1">
            <Header id={createUniqueId()} label={t(props.propertyKeys.columnOrder)} />

            <InlineMessage
              variant="warning"
              label={t(($) => $.GOOGLE_SHEETS_INTEGRATION_CONFIGURATION_COLUMN_ORDER_EDIT_WARNING)}
            />

            <div
              class="rounded-sm border bg-surface-default"
              classList={{
                "border-default": isNil(props.getError("columnOrder")),
                "border-error": !isNil(props.getError("columnOrder")),
              }}
            >
              <Show
                when={columnOrder()}
                fallback={
                  <div class="p-3">
                    <InlineMessage
                      variant="informative"
                      label={t(($) => $.GOOGLE_SHEETS_INTEGRATION_CONFIGURATION_COLUMN_ORDER_LEGACY, {
                        columnOrder: [
                          META_VARIABLES["meta.registrationDate"],
                          META_VARIABLES["info.userId"],
                          META_VARIABLES["meta.location.longitude"],
                          META_VARIABLES["meta.location.latitude"],
                          META_VARIABLES["meta.serialNumber"],
                        ]
                          .map(({ label }) => (t(label) as string).toLowerCase())
                          .join(", "),
                      })}
                    />
                  </div>
                }
              >
                {(columnOrder) => (
                  <ol
                    aria-label={t(props.propertyKeys.columnOrder)}
                    // oxlint-disable-next-line better-tailwindcss/no-restricted-classes
                    class="grid max-h-75 grid-cols-[auto_1fr] overflow-y-auto rounded-t focus-visible:outline-default"
                  >
                    <For each={columnOrder()}>
                      {(column, index) => (
                        <li aria-label={column.label} class="contents">
                          <div class="flex min-w-2 items-center justify-end rounded-tl border-r border-default bg-surface-extra-subtle px-3 text-base-regular text-secondary">
                            {letterForNumber(index())}.
                          </div>
                          <div class="flex gap-2.5 p-2.5">
                            <DynamicValueIconTile
                              icon={column.icon}
                              size="sm"
                              iconClassList={{ [column.iconClasses]: true }}
                            />
                            <Text class="text-base-regular text-primary" text={column.label} truncate />
                          </div>
                        </li>
                      )}
                    </For>
                  </ol>
                )}
              </Show>
              <div class="border-t border-default p-2">
                <TextAndIconButton
                  variant="secondary"
                  size="medium"
                  icon="pencil"
                  text={t(($) => $.GOOGLE_SHEETS_INTEGRATION_CONFIGURATION_COLUMN_ORDER_EDIT_LAYOUT)}
                  onClick={() =>
                    open({
                      content: () => (
                        <ColumnOrderEditLayoutModal
                          columnOrder={columnOrder() ?? []}
                          allColumnOrderOptions={allColumnOrderOptions}
                          onOrderChange={(order) => {
                            props.updateConfiguration("metadata", undefined);
                            props.updateConfiguration("columnOrder", order);
                          }}
                        />
                      ),
                    })
                  }
                  class="ml-auto"
                />
              </div>
            </div>
            <ErrorMessage errorMessage={props.getError("columnOrder")} />
          </div>
        </>
      );
    },
  };

type ColumnOrderEditLayoutModalProps = {
  columnOrder: ColumnOrderOption[];
  allColumnOrderOptions: ColumnOrderOption[];
  onOrderChange: (columnOrder: GoogleSheetsIntegrationConfiguration["columnOrder"]) => void;
};
const ColumnOrderEditLayoutModal: VoidComponent<ColumnOrderEditLayoutModalProps> = (props) => {
  const { t } = useTranslation();
  const { close } = useCurrentModal();

  const [query, setQuery] = createSignal("");
  const [currentItems, setCurrentItems] = createWritableMemo(() => {
    const onSelected = (id: FieldValueReference | MetaVariableReference) => (value: boolean) => {
      setCurrentItems((prev) => prev.map((item) => (item.id === id ? { ...item, selected: value } : item)));
    };

    const configured = props.columnOrder.map((item) => ({
      ...item,
      selected: true,
      onSelected: onSelected(item.id),
    }));
    const rest = props.allColumnOrderOptions
      .filter((option) => !props.columnOrder.some((configuredOption) => isEqual(option.id, configuredOption.id)))
      .map((item) => ({ ...item, selected: false, onSelected: onSelected(item.id) }));

    return [...configured, ...rest];
  });

  const hasAllItemsSelected = createMemo(() => currentItems().every((item) => item.selected));
  const toggleAllColumns = () => {
    const selected = !hasAllItemsSelected();
    setCurrentItems((prev) => prev.map((item) => ({ ...item, selected })));
  };
  const filteredItems = createMemo(() => {
    const searchQuery = query().toLowerCase();
    return currentItems().filter((item) => item.label.toLowerCase().includes(searchQuery));
  });
  const selectedItems = createMemo(() => currentItems().filter(({ selected }) => selected));
  const reOrderItem = (fromIndex: number, toIndex: number) => {
    setCurrentItems((prev) => {
      const newArr = [...prev];
      const element = newArr.splice(fromIndex, 1)[0];
      newArr.splice(toIndex, 0, element);
      return newArr;
    });
  };

  return (
    <DetailModal
      title={t(($) => $.GOOGLE_SHEETS_INTEGRATION_CONFIGURATION_COLUMN_ORDER_EDIT_LAYOUT_MODAL_TITLE)}
      description={t(($) => $.GOOGLE_SHEETS_INTEGRATION_CONFIGURATION_COLUMN_ORDER_EDIT_LAYOUT_MODAL_DESCRIPTION)}
      footer={
        <div class="flex justify-end gap-2">
          <TextButton
            variant="secondary"
            size="medium"
            text={t(($) => $.GOOGLE_SHEETS_INTEGRATION_CONFIGURATION_COLUMN_ORDER_EDIT_LAYOUT_MODAL_ACTIONS_CANCEL)}
            onClick={close}
          />
          <TextButton
            variant="primary"
            size="medium"
            text={t(($) => $.GOOGLE_SHEETS_INTEGRATION_CONFIGURATION_COLUMN_ORDER_EDIT_LAYOUT_MODAL_ACTIONS_SAVE)}
            disabled={isEmpty(selectedItems())}
            onClick={() => {
              const order = selectedItems().map(({ id: value }) =>
                value instanceof FieldValueReference
                  ? { type: "field" as const, value }
                  : { type: "metaVariable" as const, value },
              );
              props.onOrderChange(order);
              close();
            }}
          />
        </div>
      }
      content={() => (
        <>
          <div class="flex flex-col gap-2">
            <SearchBar
              aria-expanded="true"
              role="combobox"
              placeholder={t(($) => $.MENU_SEARCH_PLACEHOLDER_DEFAULT)}
              query={query()}
              onQueryChange={setQuery}
            />
            <div class="flex items-center justify-between pl-2">
              <Text
                text={t(($) => $.GOOGLE_SHEETS_INTEGRATION_CONFIGURATION_COLUMN_ORDER_EDIT_LAYOUT_MODAL_COLUMNS)}
                as="h3"
                class="text-header-md text-secondary"
              />
              <TextButton
                size="medium"
                variant="tertiary"
                onClick={toggleAllColumns}
                text={
                  hasAllItemsSelected()
                    ? t(
                        (t) =>
                          t.GOOGLE_SHEETS_INTEGRATION_CONFIGURATION_COLUMN_ORDER_EDIT_LAYOUT_MODAL_ACTIONS_DESELECT_ALL,
                      )
                    : t(
                        (t) =>
                          t.GOOGLE_SHEETS_INTEGRATION_CONFIGURATION_COLUMN_ORDER_EDIT_LAYOUT_MODAL_ACTIONS_SELECT_ALL,
                      )
                }
              />
            </div>
          </div>

          <FieldList
            items={filteredItems()}
            onDragStart={() => setQuery("")}
            onOrderUpdate={(fromIndex: number, toIndex: number) => {
              reOrderItem(fromIndex, toIndex);
            }}
          />
        </>
      )}
    />
  );
};

export default initIntegrationDefinition(GoogleSheetsIntegration);
